import { COMMA, ENTER, PERIOD } from '@angular/cdk/keycodes';
import { NgClass, SlicePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faAddressBook, faFileCirclePlus, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { AngularEditorConfig, AngularEditorModule } from '@kolkov/angular-editor';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DialogContactsData } from 'src/app/components/dialogs/contacts/models/dialog-contacts-data';
import { EmailAddressType } from 'src/app/models/enums/email-address-type';
import { Attachment } from 'src/app/models/server/attachment';
import { DraftEmail } from 'src/app/models/server/draft-email';
import { EmailAddress } from 'src/app/models/server/email-address';
import { EmailAttachment } from 'src/app/models/server/email-attachment';
import { IEmail } from 'src/app/models/server/interface/email.interface';
import { IPredefinedEmail } from 'src/app/models/server/interface/predefined-email.interface';
import { DialogService } from 'src/app/services/actions/dialog.service';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { DraftEmailsService } from 'src/app/services/http/draft-emails.service';
import { EmailAddressesService } from 'src/app/services/http/email-addresses.service';
import { EMailTemplatesService } from 'src/app/services/http/email-templates.service';
import { FoldersService } from 'src/app/services/http/folders.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { ViewsStateService } from 'src/app/services/state/views-state.service';
import { environment } from 'src/environments/environment';
import { FileUploaderCustom } from '../../../core/file-uploader-custom';
import { AvmFileSizePipe } from '../../../core/pipes/avm-file-size.pipe';
import { ContactDataType } from '../contacts/models/contact-data-type';
import { DialogDraftEmailData } from './models/dialog-draft-email-data';
import { DraftEmailActions } from './models/draft-email-actions';

@Component({
    selector: 'app-draft-email',
    templateUrl: './draft-email.component.html',
    styleUrl: './draft-email.component.scss',
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        FormsModule,
        MatOptionModule,
        NgxSpinnerModule,
        MatButtonModule,
        FaIconComponent,
        FileUploadModule,
        ReactiveFormsModule,
        MatChipsModule,
        MatIconModule,
        MatInputModule,
        NgClass,
        AngularEditorModule,
        SlicePipe,
        AvmFileSizePipe,
    ]
})
export class DraftEmailComponent implements OnInit, OnDestroy {
    /**
     * Références des icônes utilisés dans le template.
     */
    addressbookIcon = faAddressBook;
    attachmentIcon = faPaperclip;
    internalFileIcon = faFileCirclePlus;

    /**
     * Références des Enums utilisées dans le template.
     */
    public AddressType = EmailAddressType;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * URL pour le webService d'upload.
     */
    get baseUrl() {
        if (environment.production) {
            return '';
        } else {
            return environment.serverUrl;
        }
    }

    /**
     * Liste des adresses mail d'émission de l'organisation.
     */
    public senderAddresses: EmailAddress[] = [];

    /**
     * Instance de l'email en cours d'edition.
     */
    public email: IEmail;

    /**
     * Expression régulière utilisée pour valider le format d'une adresse e-mail.
     */
    public emailPattern = new RegExp(
        /^("?)((\b[\p{L}\p{N}\-_]+\b)\s?)+("?)\s?<([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)>|([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z_-]+)$/u
    );

    /**
     * Valeur indiquant si le format de l'adresse 'To' est valide.
     */
    public isToAddressFormatValid: boolean = true;

    /**
     * Valeur indiquant si le format de l'adresse 'Cc' est valide.
     */
    public isCcAddressFormatValid: boolean = true;

    /**
     * Valeur indiquant si le format de l'adresse 'Cci' est valide.
     */
    public isCciAddressFormatValid: boolean = true;

    /**
     * Champs du formulaire d'envoi de mail.
     */
    public subjectFormControl = new UntypedFormControl(null, [Validators.required]);
    public bodyFormControl = new UntypedFormControl(null, [Validators.required]);
    public senderAddressFormControl = new UntypedFormControl(null);
    public addressToFormControl = new UntypedFormControl(null, [Validators.required]);
    public addressCcFormControl = new UntypedFormControl(null);
    public addressCciFormControl = new UntypedFormControl(null);
    public addressToInputFormControl = new UntypedFormControl(null);
    public addressCcInputFormControl = new UntypedFormControl(null);
    public addressCciInputFormControl = new UntypedFormControl(null);

    /**
     * Touches utilisées pour valider la saisie d'une adresse e-mail.
     * PERIOD: point-virgule
     */
    readonly separatorKeysCodes: number[] = [ENTER, COMMA, PERIOD];

    /**
     * Configuration de l'editeur de texte utilisé pour saisir le corps de l'e-mail.
     */
    config: AngularEditorConfig = {
        editable: true,
        spellcheck: true,
        height: 'auto',
        placeholder: 'Ecrire votre message ici ...',
        translate: 'no',
        defaultParagraphSeparator: 'DIV',
        defaultFontName: 'Arial',
        toolbarHiddenButtons: [['insertVideo']],
        customClasses: [
            {
                name: 'quote',
                class: 'quote',
            },
            {
                name: 'redText',
                class: 'redText',
            },
            {
                name: 'titleText',
                class: 'titleText',
                tag: 'h1',
            },
        ],
    };

    /**
     * Composant d'upload de fichiers.
     */
    public uploader: FileUploaderCustom = null;

    /**
     * Liste des fichiers du dossier non sélectionnés
     */
    private folderFilesNotSelected: Attachment[] = [];

    /**
     * Liste des fichiers du dossiers sélectionnés
     */
    public folderFilesSelected: Attachment[] = [];

    /**
     * Action de l'utilisateur (correspond au bouton cliqué par l'utilisateur).
     */
    private userAction: DraftEmailActions = undefined;

    /**
     * Constructeur.
     * @param connectedUserStateService
     * @param mailtemplateService
     * @param dialogService
     * @param dialogRef
     * @param data
     */
    constructor(
        private connectedUserStateService: ConnectedUsersStateService,
        private viewsStateService: ViewsStateService,
        private mailtemplateService: EMailTemplatesService,
        private emailAddressesService: EmailAddressesService,
        public folderService: FoldersService,
        private draftEmailsService: DraftEmailsService,
        private dialogService: DialogService,
        private spinner: SpinnerService,
        public dialogRef: MatDialogRef<DraftEmailComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogDraftEmailData
    ) {}

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.email = {
            fromAddressId: -1,
            to: [],
            cc: [],
            cci: [],
            subject: '',
            body: '',
            folderFiles: [],
        };

        this.loadSenderAddresses();

        this.loadFolderFiles();

        this.InitUploadComponent();

        this.applyDraftEmail();

        this.addressToFormControl.valueChanges.subscribe((v) => {
            if (this.isToAddressFormatValid === false) {
                this.addressToFormControl.setErrors({ invalidFormat: true });
            }
        });

        this.addressCcFormControl.valueChanges.subscribe((v) => {
            if (this.isCcAddressFormatValid === false) {
                this.addressCcFormControl.setErrors({ invalidFormat: true });
            }
        });

        this.addressCciFormControl.valueChanges.subscribe((v) => {
            if (this.isCciAddressFormatValid === false) {
                this.addressCciFormControl.setErrors({ invalidFormat: true });
            }
        });
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelée lorsque l'utilisateur valide la saisie d'une adresse e-mail avec la touche ENTREE, virgule, point-virgule ou en quittant le champ de saisie.
     * @param event : Événement émis par le composant de saisie d'adresse e-mail.
     * @param AddressType : Type d'adresse.
     */
    public addAddress(event: MatChipInputEvent, addressType: EmailAddressType): void {
        const input = event.chipInput.inputElement;
        const value = event.value.trim();

        if (value !== '') {
            if (!!value && this.emailPattern.test(value) === true) {
                input.value = '';
                switch (addressType) {
                    case EmailAddressType.to:
                        this.email.to.push(value);
                        this.isToAddressFormatValid = true;
                        this.addressToFormControl.markAsPristine();
                        this.addressToFormControl.markAsUntouched();
                        break;
                    case EmailAddressType.cc:
                        this.email.cc.push(value);
                        this.isCcAddressFormatValid = true;
                        this.addressCcFormControl.markAsPristine();
                        this.addressCcFormControl.markAsUntouched();
                        break;
                    case EmailAddressType.cci:
                        this.email.cci.push(value);
                        this.isCciAddressFormatValid = true;
                        this.addressCciFormControl.markAsPristine();
                        this.addressCciFormControl.markAsUntouched();
                        break;
                }
            } else {
                switch (addressType) {
                    case EmailAddressType.to:
                        this.isToAddressFormatValid = false;
                        break;
                    case EmailAddressType.cc:
                        this.isCcAddressFormatValid = false;
                        break;
                    case EmailAddressType.cci:
                        this.isCciAddressFormatValid = false;
                        break;
                }
            }
        } else {
            switch (addressType) {
                case EmailAddressType.to:
                    this.isToAddressFormatValid = true;
                    break;
                case EmailAddressType.cc:
                    this.isCcAddressFormatValid = true;
                    break;
                case EmailAddressType.cci:
                    this.isCciAddressFormatValid = true;
                    break;
            }
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône de suppression d'une adresse e-mail.
     * @param address : adresse e-mail à supprimer.
     * @param addressType : Type d'adresse.
     */
    public removeAddress(address: string, addressType: EmailAddressType): void {
        switch (addressType) {
            case EmailAddressType.to:
                this.email.to.splice(this.email.to.indexOf(address), 1);

                if (this.email.to.length == 0) {
                    // Necessaire pour déclencher le validateur Requiered.
                    this.addressToFormControl.setValue('');
                }
                break;
            case EmailAddressType.cc:
                this.email.cc.splice(this.email.cc.indexOf(address), 1);
                break;
            case EmailAddressType.cci:
                this.email.cci.splice(this.email.cci.indexOf(address), 1);
                break;
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'icône des contacts.
     */
    public displayContacts(): void {
        this.dialogService
            .openContacts(ContactDataType.Email)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x: DialogContactsData) => {
                if (!!x) {
                    if (this.email.to.length == 0) {
                        this.isToAddressFormatValid = true;
                    }

                    x.toAddresses.forEach((to) => {
                        this.email.to.push(to);
                    });

                    x.ccAddresses.forEach((cc) => {
                        this.email.cc.push(cc);
                    });

                    x.cciAddresses.forEach((cci) => {
                        this.email.cci.push(cci);
                    });
                }
            });
    }

    /**
     * Appelée lorsque l'utilisateur sélectionne un modèle d'e-mail.
     * @param templateId : Identifiant du modèle d'e-mail sélectionné.
     */
    public applyDraftEmail(): void {
        var email = JSON.parse(this.data.draftEmail.content) as IPredefinedEmail;
        var attachments = EmailAttachment.fromArray(JSON.parse(this.data.draftEmail.attachments));

        if (email.fromAddressId === null) {
            this.email.fromAddressId = -1;
        } else {
            this.email.fromAddressId = email.fromAddressId;
        }
        this.senderAddressFormControl.setValue(this.email.fromAddressId);

        this.email.to = [];
        this.addressToInputFormControl.setValue('');

        if (!!email.to) {
            email.to.forEach((to) => {
                this.email.to.push(to);
            });

            this.addressToFormControl.setValue(this.email.to);
        }
        this.addressToFormControl.markAsDirty();
        this.addressToFormControl.markAsTouched();
        this.addressToFormControl.setErrors(null);
        this.addressToFormControl.updateValueAndValidity();

        this.email.cc = [];
        this.addressCcInputFormControl.setValue('');

        if (!!email.cc) {
            email.cc.forEach((cc) => {
                this.email.cc.push(cc);
            });
        }
        this.addressCcFormControl.markAsTouched();
        this.addressCcFormControl.setErrors(null);

        this.email.cci = [];
        this.addressCciInputFormControl.setValue('');

        if (!!email.cci) {
            email.cci.forEach((cci) => {
                this.email.cci.push(cci);
            });
        }
        this.addressCciFormControl.markAsTouched();
        this.addressCciFormControl.setErrors(null);

        this.email.subject = email.subject;
        this.subjectFormControl.setValue(this.email.subject);
        this.subjectFormControl.markAsTouched();
        this.subjectFormControl.updateValueAndValidity();
        this.email.body = email.body;
        this.bodyFormControl.setValue(this.email.body);
        this.bodyFormControl.markAsTouched();
        this.bodyFormControl.updateValueAndValidity();

        this.uploader.clearQueue();

        if (attachments && attachments.length > 0) {
            attachments.forEach((x) => {
                this.draftEmailsService.getAttachmentFile(this.data.draftEmail.viewId, this.data.draftEmail.folderId, this.data.draftEmail.id, x.id).subscribe((y) => {
                    let attachmentFile = new File([y], x.fileName, {
                        type: y.type,
                    });
                    this.uploader.addToQueue([attachmentFile]);
                    this.spinner.hide('send-email-spinner');
                });
            });
        } else {
            this.spinner.hide('send-email-spinner');
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.userAction = DraftEmailActions.Canceled;

        let result = {
            draftEmail: this.data.draftEmail,
            action: DraftEmailActions.Canceled,
        };

        this.dialogRef.close(result);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Supprimer'.
     */
    public delete(): void {
        this.userAction = DraftEmailActions.Deleted;

        let result = {
            draftEmail: this.data.draftEmail,
            action: DraftEmailActions.Deleted,
        };

        this.dialogRef.close(result);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Enregistrer le brouillon'.
     */
    public save(): void {
        this.spinner.show('send-email-spinner');
        this.fillData();

        // Tricks : L'envoi de l'email se fait via le formulaire de la requête multipart (les propriétés de l'object Email sont envoyées sous forme de clé / valeur ou la valeur ne peut etre qu'un type primitif). Il n'est donc pas possible de transmettre un tableau d'Attachment. Du coup on convertit le tableau d'Attachment en JSON et on passe le texte JSON comme valeur. Pour forcer le texte JSON dans la propriété de type Attachment on la cast en any.
        this.email['folderFiles'] = JSON.stringify(this.folderFilesSelected) as any;

        this.userAction = DraftEmailActions.Saved;

        const user = this.connectedUserStateService.getUserConnected();

        this.uploader.options.method = 'PUT';
        this.uploader.options.url = `${this.baseUrl}/api/organizations/${user.organization.id}/views/${this.data.draftEmail.viewId}/folders/${this.data.draftEmail.folderId}/draft-emails/${this.data.draftEmail.id}`;

        this.data.draftEmail.content = JSON.stringify(this.email);

        this.uploader.uploadAllFiles(this.email);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Valider'.
     */
    public send(): void {
        if (
            this.email.to.length > 0 &&
            this.subjectFormControl.valid &&
            this.bodyFormControl.valid &&
            this.isToAddressFormatValid &&
            this.isCcAddressFormatValid &&
            this.isCciAddressFormatValid
        ) {
            this.spinner.show('send-email-spinner');

            this.fillData();

            this.email.folderFiles = this.folderFilesSelected;

            this.userAction = DraftEmailActions.Sended;
            const user = this.connectedUserStateService.getUserConnected();

            this.uploader.options.method = 'POST';
            this.uploader.options.url = `${this.baseUrl}/api/organizations/${user.organization.id}/views/${this.data.draftEmail.viewId}/folders/${this.data.draftEmail.folderId}/email?draftId=${this.data.draftEmail.id}`;

            this.uploader.uploadAllFiles(this.email);
        } else {
            this.addressToFormControl.markAsTouched();
            this.addressToFormControl.updateValueAndValidity();
            this.addressCcFormControl.markAsTouched();
            this.addressCciFormControl.markAsTouched();
            this.subjectFormControl.markAsTouched();
            this.bodyFormControl.markAsTouched();
        }
    }

    /**
     * Adresses emails à ajouter lors d'un copier/coller
     * @param event copier/coller
     */
    addressesToPaste(event: ClipboardEvent, addressType: EmailAddressType): void {
        event.preventDefault();
        event.clipboardData
            .getData('Text')
            .split(/;|,|\n/)
            .forEach((value) => {
                if (value.trim() && this.emailPattern.test(value.trim()) === true) {
                    const mail = this.emailPattern.exec(value.trim())[0];
                    switch (addressType) {
                        case EmailAddressType.to:
                            this.email.to.push(mail);
                            this.isToAddressFormatValid = true;
                            break;
                        case EmailAddressType.cc:
                            this.email.cc.push(mail);
                            this.isCcAddressFormatValid = true;
                            break;
                        case EmailAddressType.cci:
                            this.email.cci.push(mail);
                            this.isCciAddressFormatValid = true;
                            break;
                    }
                }
            });
    }

    /**
     * Initialise le composant d'upload de fichiers.
     */
    private InitUploadComponent() {
        const user = this.connectedUserStateService.getUserConnected();
        this.uploader = new FileUploaderCustom({
            url: `${this.baseUrl}/api/organizations/${user.organization.id}/views/${this.data.draftEmail.viewId}/folders/${this.data.draftEmail.folderId}/email?draftId=${this.data.draftEmail.id}`,
            authTokenHeader: 'Authorization',
            authToken: `Bearer ${user.token.accessToken}`,
        });

        this.uploader.onSuccessItem = (fileItem, response) => {
            this.spinner.hide('send-email-spinner');
            let draft: DraftEmail = null;

            if (this.userAction === DraftEmailActions.Saved) {
                draft = DraftEmail.from(JSON.parse(response));
            }

            let result = {
                draftEmail: draft,
                action: this.userAction,
            };

            this.dialogRef.close(result);
        };
    }

    /**
     * Charge les modèles d'e-mails en mémoire.
     */
    private loadSenderAddresses() {
        this.emailAddressesService
            .ListSendingEmailAddress()
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.senderAddresses = x;
            });
    }

    /**
     * Charge les fichiers d'un dossier.
     */
    private loadFolderFiles() {
        this.folderService.getFolderFiles(this.data.draftEmail.folderId).subscribe((x) => {
            // Exclu les corps des mails
            this.folderFilesNotSelected = x.filter((x) => x.name !== 'body.txt');
        });
    }

    private fillData() {
        this.email.fromAddressId = this.senderAddressFormControl.value !== -1 ? this.senderAddressFormControl.value : null;
        this.email.subject = this.subjectFormControl.value;
        this.email.body = this.bodyFormControl.value;
    }

    /**
     * Lors du click pour ajouter un fichier du dossier.
     */
    public internalFileToAdd(): void {
        this.dialogService
            .openSelectFolderFiles(this.folderFilesNotSelected)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x: Attachment[]) => {
                if (x) {
                    this.folderFilesSelected.push(...x);

                    this.folderFilesSelected.forEach((a) => {
                        this.folderFilesNotSelected.splice(
                            this.folderFilesNotSelected.findIndex((b) => b.id === a.id),
                            1
                        );
                    });
                }
            });
    }

    /**
     * Lors de la suppression d'un fichier de dossier.
     * @param attachment fichier de dossier à supprimer.
     */
    public deleteFolderFile(attachment: Attachment) {
        this.folderFilesSelected.splice(
            this.folderFilesSelected.findIndex((a) => a.id === attachment.id),
            1
        );
        this.folderFilesNotSelected.push(attachment);
    }
}
